import React from "react";
import companyLogo from "../../../assets/images/company-logo.png";
import html2pdf from "html2pdf.js";
import "../income/IncomeProposalPreview.css"; // 기존 CSS 그대로 재사용

/**
 * 모든 기안/결의 문서가 공유하는 "종이 껍데기".
 * 로고 · 수신/경유/제목 · 결재란 · 시행/주소/연락처는 문서 종류와 무관하게 동일하므로
 * 여기서 한 번만 그린다. 가운데 내용(content-section)만 children으로 갈아끼운다.
 *
 * props:
 *   title          : 제목
 *   documentNumber : 시행(기안)번호
 *   approvalLine   : [{ id, position, name, status }]
 *   onSave, saving : 저장 버튼용 (없으면 버튼 숨김)
 *   extraActions   : 헤더 버튼 줄 맨 앞에 끼워 넣을 추가 버튼(선택) — 예: 인쇄 버튼.
 *                    기본 버튼들과 같은 줄에 나란히 배치되고, .preview-header가
 *                    이미 @media print로 숨겨지므로 별도 처리 없이 인쇄 시 숨김.
 *   hidePreviewButton : true면 기본 제공 "PDF 미리보기" 버튼을 숨김(선택, 기본 false —
 *                    기존 호출부는 전부 그대로 렌더).
 *   children       : 문서 종류별 본문
 */
export default function PreviewPaper({
  title,
  documentNumber,
  approvalLine = [],
  onSave,
  saving = false,
  extraActions,
  hidePreviewButton = false,
  children,
}) {
  const sorted = [...approvalLine].sort((a, b) => {
    const order = { 기안: 1, 합의: 2, 결재: 3 };
    return (order[a.status] || 999) - (order[b.status] || 999);
  });

  // PDF 미리보기/다운로드 공통 처리
  const withPaper = (run) => {
    const el = document.querySelector(".preview-paper");
    if (!el) return;
    const original = el.style.height;
    el.style.height = "297mm";
    el.style.minHeight = "297mm";
    el.style.maxHeight = "297mm";
    const opt = {
      margin: 0,
      filename: `${title || "기안서"}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        width: 794,
        height: 1123,
      },
      jsPDF: { unit: "px", format: [794, 1123], orientation: "portrait", hotfixes: ["px_scaling"] },
    };
    run(html2pdf().set(opt).from(el))
      .then(() => (el.style.height = original))
      .catch(() => (el.style.height = original));
  };

  const handlePreview = () =>
    withPaper((chain) =>
      chain.outputPdf("blob").then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      })
    );

  const handleDownload = () => withPaper((chain) => chain.save());

  return (
    <div className="income-preview-wrapper">
      {/* 헤더 (저장 + PDF 버튼) */}
      <div className="preview-header">
        <h3 className="preview-title">📄 미리보기</h3>
        <div style={{ display: "flex", gap: "10px" }}>
          {extraActions}
          {onSave && (
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="print-button save-button"
            >
              {saving ? "⏳ 저장중..." : "💾 저장"}
            </button>
          )}
          {!hidePreviewButton && (
            <button type="button" onClick={handlePreview} className="print-button preview-button">
              👁️ PDF 미리보기
            </button>
          )}
          <button type="button" onClick={handleDownload} className="print-button download-button">
            📥 PDF 다운로드
          </button>
        </div>
      </div>

      <div className="income-preview-container">
        <div className="preview-paper">
          {/* 1. 로고 */}
          <div className="preview-logo-section">
            <div className="company-logo">
              <img src={companyLogo} alt="금천일자리 주식회사" className="logo-image" />
            </div>
          </div>

          {/* 2~4. 수신 / 경유 / 제목 */}
          <div className="preview-field-row">
            <span className="field-label">수&nbsp;&nbsp;&nbsp;&nbsp;신</span>
            <span className="field-value">내부결재</span>
          </div>
          <div className="preview-field-row">
            <span className="field-label">(경&nbsp;&nbsp;유)</span>
            <span className="field-value"></span>
          </div>
          <div className="preview-field-row title-row">
            <span className="field-label">제&nbsp;&nbsp;&nbsp;&nbsp;목</span>
            <span className="field-value">{title || "제목을 입력하세요"}</span>
          </div>

          {/* ▼ 문서 종류별로 갈아끼우는 본문 */}
          <div className="preview-content-section">{children}</div>

          {/* 7. 결재라인 */}
          <div className="preview-approval-line-simple">
            {sorted
              .filter((a) => a.status !== "합의")
              .map((a, i) => (
                <React.Fragment key={a.id ?? i}>
                  {i > 0 && "          "}
                  <span>
                    {a.position}&nbsp;&nbsp;{a.name}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </span>
                </React.Fragment>
              ))}
          </div>

          {/* 8. 협조자 */}
          <div className="preview-cooperator-line">
            {sorted
              .filter((a) => a.status === "합의")
              .map((a, i) => (
                <React.Fragment key={a.id ?? i}>
                  {i > 0 && "          "}
                  <span>
                    {a.position}  {a.name}
                  </span>
                </React.Fragment>
              ))}
          </div>

          {/* 9. 시행/기안번호 */}
          <div className="preview-document-info">
            <div className="doc-info-row">
              <span className="doc-label">시&nbsp;&nbsp;행</span>
              <span className="doc-value">
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {documentNumber?.replace(/-[가-힣]+/g, "")?.replace(/\//g, "") || ""}
              </span>
            </div>
          </div>

          {/* 10. 주소 */}
          <div className="preview-address-section">
            <div className="address-row">
              <span className="zip-label">우&nbsp;&nbsp;편</span>
              <span className="zip-code">08592</span>
              <span className="address-text">
                &nbsp;서울시 금천구 가산디지털2로 14, 201호(가산동, 대륭테크노타운12차) /
              </span>
            </div>
          </div>

          {/* 11. 연락처 */}
          <div className="preview-contact-info">
            <span className="contact-item">전&nbsp;&nbsp;화  02-854-9920</span>
            <span className="contact-divider">/</span>
            <span className="contact-item">전&nbsp;&nbsp;송  02-854-9921</span>
            <span className="contact-divider">/</span>
            <span className="contact-item">이메일 : gcjob@naver.com</span>
            <span className="contact-divider">/</span>
            <span className="contact-item">공개</span>
          </div>
        </div>
      </div>
    </div>
  );
}